import { css } from '@emotion/core'
import { useFeatureFlags } from '@fs/zion-flags'
import {
  createFieldValue,
  generateSls,
  getImage,
  getMostRecentFieldValue,
  getNestedOrFloatingElementById,
  INDEXED_INCORRECTLY,
  saveSlsAndUpdateState,
  useSlsDispatchContext,
  useSlsStateContext,
} from '@fs/zion-sls'
import { Button, colors, QuickGlanceOverlay, Row, TextField, useOverlay } from '@fs/zion-ui'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { splunk } from '@fs/zion-splunk-logger'
import { useSharedBrowserStateContext } from '../../../../../shared/providers/sharedBrowserProvider/SharedBrowserContext'
import { useR2Highlights, useR2StateContext } from '../../../../providers/r2Provider/R2Context'
import { FullTextApp, headers } from '../../../../../shared/services/config'
import { setDisplayHighlights } from '../../../../utils/imageHighlightUtils'
import { useFullTextStateContext } from '../../providers/FullTextContext'

const keywordCss = css`
  font-weight: bold;
  padding: 0 4px;
  border-radius: 4px;
  background: none;
  border: none;
`

const buttonCss = css`
  background: none;
  border: none;
  padding: 0 2px;
`

const editableCss = css`
  border: 1px solid ${colors.gray10};
  border-radius: 4px;
  padding: 5px;
`

const redactedCss = css`
  color: ${colors.red50};
`

const Transcript = ({
  fields,
  canViewRedactedContent,
  keywordFields,
  showKeywords,
  allHighlights,
  selectPersonToAttachToTreeInfoSheetOverlay,
}) => {
  const [t] = useTranslation()
  const state = useSlsStateContext()
  const dispatch = useSlsDispatchContext()
  const r2State = useR2StateContext()
  const fullTextState = useFullTextStateContext()
  const { thumbnails } = useSharedBrowserStateContext()
  const [editingWord, setEditingWord] = useState(null)
  const [editingWordValue, setEditingWordValue] = useState('')
  const [originalValue, setOriginalValue] = useState('')
  const editQuickGlanceOverlay = useOverlay()
  const {
    rip_sharedImageViewer_saveChanges: saveChangesFlag,
    rip_sharedImageViewer_sls_actions_splunk_debugger: slsActionsSplunkDebugger,
  } = useFeatureFlags(['rip_sharedImageViewer_saveChanges', 'rip_sharedImageViewer_sls_actions_splunk_debugger'])

  const currentImage = useMemo(() => getImage(state?.images, state?.currentImageArk) || {}, [state])

  const { displayHighlightsApi, splitScreenDisplayHighlightsApi } = useR2Highlights()

  const setHighlights = useCallback(() => {
    const filterEditHighlights = (imageArk, highlights) => {
      return (
        highlights
          ?.filter((highlight) => {
            if (highlight.imageId === imageArk && highlight.fieldId) {
              const field = keywordFields?.find((f) => f.id === highlight.fieldId)
              return field
            }
            return false
          })
          ?.map((highlight) => ({ ...highlight, attached: true })) || []
      )
    }
    if (showKeywords) {
      const imageHighlights = filterEditHighlights(state.currentImageArk, allHighlights)
      setDisplayHighlights(imageHighlights, displayHighlightsApi)
      if (r2State.splitScreen) {
        setDisplayHighlights(
          filterEditHighlights(thumbnails?.[r2State.referenceFocusedIndex]?.ark, r2State.referenceHighlights),
          splitScreenDisplayHighlightsApi
        )
      }
    } else {
      setDisplayHighlights(null, displayHighlightsApi)
      setDisplayHighlights(null, splitScreenDisplayHighlightsApi)
    }
  }, [
    allHighlights,
    displayHighlightsApi,
    r2State.referenceFocusedIndex,
    r2State.referenceHighlights,
    r2State.splitScreen,
    splitScreenDisplayHighlightsApi,
    state.currentImageArk,
    thumbnails,
    showKeywords,
    keywordFields,
  ])

  const transcript = useMemo(() => {
    const getFieldKey = (field, index) => {
      let key = field.id
      if (field.superElements?.[0]) {
        key += `_${field.superElements[0].id}`
      }
      key += `_${index}`
      return key
    }

    const clickHandler = (event, word) => {
      editQuickGlanceOverlay.handleClick(event)
      setEditingWord(word)
      const currentValue = getMostRecentFieldValue(word)
      setEditingWordValue(currentValue)
      setOriginalValue(currentValue)
    }

    const renderRedactedField = (field, key) => {
      const redactedText = canViewRedactedContent ? getMostRecentFieldValue(field) : null
      const redactedLabel = t('full-text.redacted', 'Content redacted')
      return (
        <span
          key={key}
          css={redactedText ? redactedCss : undefined}
          title={redactedText ? undefined : redactedLabel}
          aria-label={redactedText ? undefined : redactedLabel}
        >
          {redactedText ? `${redactedText} ` : ' "■■■■■" '}
        </span>
      )
    }

    const handleFieldMouseOver = (fieldId) => {
      if (!selectPersonToAttachToTreeInfoSheetOverlay.isOpen) {
        const highlights = allHighlights.filter((hl) => hl.fieldId === fieldId).map((hl) => hl.id)
        displayHighlightsApi.hover(highlights)
        if (r2State.splitScreen) {
          const splitScreenHighlights = r2State.referenceHighlights.filter((hl) => hl.fieldId === fieldId)
          splitScreenDisplayHighlightsApi.hover(splitScreenHighlights)
        }
      }
    }

    const handleFieldMouseOut = () => {
      if (!selectPersonToAttachToTreeInfoSheetOverlay.isOpen) {
        displayHighlightsApi.hover(null)
        if (r2State.splitScreen) {
          splitScreenDisplayHighlightsApi.hover(null)
        }
      }
    }

    const renderKeywordField = (field, key) => {
      const highlightColor = field.id === r2State.activeFieldId ? colors.blue05 : colors.yellow05
      const onMouseOver = () => handleFieldMouseOver(field.id)
      const onMouseOut = handleFieldMouseOut
      if (fullTextState.editTranscriptMode) {
        return (
          <span key={key}>
            {showKeywords && (
              <button
                css={keywordCss}
                type="button"
                style={{ backgroundColor: highlightColor }}
                onMouseOver={onMouseOver}
                onFocus={onMouseOver}
                onMouseOut={onMouseOut}
                onBlur={onMouseOut}
                onClick={(event) => clickHandler(event, field)}
              >
                {`${getMostRecentFieldValue(field)} `}
              </button>
            )}
            {!showKeywords && (
              <button
                type="button"
                css={buttonCss}
                onClick={(event) => clickHandler(event, field)}
              >{`${getMostRecentFieldValue(field)} `}</button>
            )}
          </span>
        )
      }
      return (
        <span key={key}>
          {showKeywords && (
            <span
              css={keywordCss}
              style={{ backgroundColor: highlightColor }}
              onMouseOver={onMouseOver}
              onFocus={onMouseOver}
              onMouseOut={onMouseOut}
              onBlur={onMouseOut}
            >
              {`${getMostRecentFieldValue(field)} `}
            </span>
          )}
          {!showKeywords && <span>{`${getMostRecentFieldValue(field)} `}</span>}
        </span>
      )
    }

    return fields?.map((field, index) => {
      const key = getFieldKey(field, index)
      if (field.redacted) return renderRedactedField(field, key)
      if (field.isKeyword) return renderKeywordField(field, key)
      return fullTextState.editTranscriptMode ? (
        <button
          key={key}
          type="button"
          css={buttonCss}
          onClick={(event) => clickHandler(event, field)}
        >{`${getMostRecentFieldValue(field)} `}</button>
      ) : (
        <span key={key}>{`${getMostRecentFieldValue(field)} `}</span>
      )
    })
    // editQuickGlanceOverlay is changing on every render
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    fields,
    displayHighlightsApi,
    splitScreenDisplayHighlightsApi,
    allHighlights,
    r2State.activeFieldId,
    r2State.referenceHighlights,
    r2State.splitScreen,
    showKeywords,
    selectPersonToAttachToTreeInfoSheetOverlay.isOpen,
    fullTextState.editTranscriptMode,
  ])

  const editTranscriptField = async () => {
    const id = editingWord.id.replace(/_\d+$/, '')
    const field = getNestedOrFloatingElementById([currentImage], id)
    const splitIdString = editingWord.id.split('_')
    const wordIndex = splitIdString.length > 1 ? splitIdString[splitIdString.length - 1] : 0
    const oldFieldValue = getMostRecentFieldValue(field)
    const splitFieldValue = oldFieldValue.split(' ')
    splitFieldValue[wordIndex] = editingWordValue
    const newFieldValue = splitFieldValue.join(' ')
    let dispatches = []

    if (newFieldValue !== oldFieldValue) {
      dispatches = [
        {
          type: 'ADD_FV',
          value: {
            partitionKey: currentImage.partitionKey,
            fieldValue: createFieldValue(newFieldValue, {
              comment: '',
              reasonId: INDEXED_INCORRECTLY,
              app: FullTextApp,
            }),
            id,
          },
        },
      ]
    }

    const sls = generateSls(dispatches)
    await saveSlsAndUpdateState({
      sls,
      dispatches,
      saveChangesFlag,
      slsActionsSplunkDebugger,
      fsUserAgentChain: headers['FS-User-Agent-Chain'],
      userId: r2State.userId,
      appName: FullTextApp,
      dispatch,
      successHandler: () => {
        setEditingWord(null)
        splunk.log(
          `Edited transcript field id: ${editingWord?.id}, oldFieldValue: ${oldFieldValue}, newFieldValue: ${newFieldValue}, partitionKey: ${currentImage?.partitionKey}`
        )
        editQuickGlanceOverlay.close()
      },
      saveInitiatorFilename: 'Transcript.js',
    })
  }

  useEffect(() => {
    if (!selectPersonToAttachToTreeInfoSheetOverlay.isOpen) {
      setHighlights()
    }
  }, [setHighlights, selectPersonToAttachToTreeInfoSheetOverlay.isOpen])

  return (
    <>
      <div data-testid="full-text-transcript">
        {fullTextState.editTranscriptMode ? <div css={editableCss}>{transcript}</div> : transcript}
      </div>
      <QuickGlanceOverlay {...editQuickGlanceOverlay}>
        <Row>
          <TextField
            data-testid="edit-transcript-input"
            value={editingWordValue}
            onChange={(event) => setEditingWordValue(event.target.value)}
          />
          <Button
            data-test-id="full-text-transcript-submit-edit"
            linkName="transcript-edit-submit"
            disabled={editingWordValue === originalValue}
            onClick={editTranscriptField}
            emphasis="high"
          >
            {t('full-text.submit.button', 'Submit')}
          </Button>
        </Row>
      </QuickGlanceOverlay>
    </>
  )
}

export default Transcript
